import { Alert, Button, Progress, Space } from 'antd';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '@/hooks/usePlan';
import { ROUTES } from '@/constants/routes';
import type { PlanLimits } from '@/redux/slices/planSlice';

/**
 * Trial banner — shows while plan is in trial (trialDaysLeft > 0).
 */
export function TrialBanner() {
  const { inTrial, trialDaysLeft } = usePlan();
  const navigate = useNavigate();

  if (!inTrial) return null;

  return (
    <Alert
      type="info"
      showIcon
      icon={<ClockCircleOutlined />}
      message={`Bạn đang dùng thử gói ${trialDaysLeft ?? 0} ngày còn lại`}
      action={
        <Button size="small" type="primary" onClick={() => navigate(ROUTES.settings.billing)}>
          Nâng cấp
        </Button>
      }
      style={{ marginBottom: 12 }}
    />
  );
}

/**
 * Grace banner — after expiry, before hard block (graceDaysLeft > 0).
 */
export function GraceBanner() {
  const { inGrace, graceDaysLeft } = usePlan();
  const navigate = useNavigate();

  if (!inGrace) return null;

  return (
    <Alert
      type="warning"
      showIcon
      message={`Gói đã hết hạn — còn ${graceDaysLeft ?? 0} ngày ân hạn. Vui lòng gia hạn để tránh gián đoạn.`}
      action={
        <Button size="small" type="primary" danger onClick={() => navigate(ROUTES.settings.billing)}>
          Gia hạn ngay
        </Button>
      }
      style={{ marginBottom: 12 }}
    />
  );
}

interface LimitWarningProps {
  /** Limit key to check. */
  resource: keyof PlanLimits;
  /** Threshold (%) to show warning. Default 80. */
  threshold?: number;
  /** Label shown to user (optional override). */
  label?: string;
}

/**
 * LimitWarning — soft warning when usage ≥ threshold% of plan limit.
 * Hard-blocks happen at call site via isOverLimit().
 */
export function LimitWarning({ resource, threshold = 80, label }: LimitWarningProps) {
  const { usage, limits, usagePercent } = usePlan();
  const navigate = useNavigate();

  const limit = limits[resource];
  if (limit === -1) return null;
  const pct = usagePercent(resource);
  if (pct < threshold) return null;

  const over = pct >= 100;
  const resourceLabels: Record<keyof PlanLimits, string> = {
    products: 'sản phẩm',
    staffs: 'nhân viên',
    stores: 'cửa hàng',
    monthlyOrders: 'đơn/tháng',
  };
  const resLabel = label ?? resourceLabels[resource];

  return (
    <Alert
      type={over ? 'error' : 'warning'}
      showIcon
      icon={<WarningOutlined />}
      message={
        <Space>
          <span>
            {over ? 'Đã đạt' : 'Sắp đạt'} giới hạn {resLabel}: {usage[resource]} / {limit}
          </span>
          <Progress
            percent={pct}
            size="small"
            status={over ? 'exception' : 'active'}
            style={{ width: 120, marginBottom: 0 }}
          />
        </Space>
      }
      action={
        <Button size="small" type="primary" onClick={() => navigate(ROUTES.settings.billing)}>
          Nâng cấp
        </Button>
      }
      style={{ marginBottom: 12 }}
    />
  );
}

/**
 * OfflineBanner — persistent warning when browser goes offline (EG-1042).
 * Shows: "⚠ Mất kết nối mạng. Một số chức năng có thể không hoạt động"
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOffline = () => setOffline(true);
    const handleOnline = () => setOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      message="Mất kết nối mạng. Một số chức năng có thể không hoạt động"
      style={{ marginBottom: 12 }}
    />
  );
}

import { onboardingSelectors } from '@/redux/slices/onboardingSlice';

export function SampleDataBanner() {
  const hasSampleData = useSelector(onboardingSelectors.hasSampleData);

  if (!hasSampleData) return null;

  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      message="Đây là dữ liệu mẫu — hãy xóa trước khi bắt đầu kinh doanh thật"
      style={{ marginBottom: 12 }}
    />
  );
}